import { Case, CaseOrderSummary, OrderStatuses } from "@types-runway/case";

export interface AccountOrder {
  order: CaseOrderSummary;
  caseRecord: Case;
}

const activeStatuses: string[] = [
  OrderStatuses.NEW,
  OrderStatuses.IN_PROGRESS,
  OrderStatuses.OUT_FOR_DELIVERY,
];

const createdTime = ({ createdAt }: CaseOrderSummary) =>
  createdAt ? new Date(createdAt).getTime() : 0;

const isNewer = (candidate: CaseOrderSummary, current: CaseOrderSummary) =>
  createdTime(candidate) - createdTime(current) > 0 ||
  (createdTime(candidate) === createdTime(current) && candidate.id > current.id);

// ACCT-R2: the most recently created of all the User's Orders, on any Case; Orders sharing a
// creation time rank by number, as the backend lists them.
export const findLatestOrder = (cases: Case[]): AccountOrder | null =>
  cases
    .flatMap((caseRecord) => (caseRecord.orders ?? []).map((order) => ({ order, caseRecord })))
    .reduce<AccountOrder | null>(
      (latest, candidate) =>
        latest === null || isNewer(candidate.order, latest.order) ? candidate : latest,
      null
    );

const countOf = (count: number, noun: string) => `${count} ${noun}${count === 1 ? "" : "s"}`;

// ACCT-R7: "N Cases · M Orders · K active", an Order being active while new, in progress or out
// for delivery; one Case or Order reads in the singular, as "1 case" does in Case Search (CASES-R6).
export const countAccount = (cases: Case[]) => {
  const orders = cases.flatMap((caseRecord) => caseRecord.orders ?? []);
  const activeCount = orders.filter(({ status }) => activeStatuses.includes(status)).length;

  return `${countOf(cases.length, "Case")} · ${countOf(orders.length, "Order")} · ${activeCount} active`;
};
